import type { MissionChoice } from "../api/client";
import type { DeployedUnit, Deployment, MissionCatalogueEntry, Phase, UnitPosition } from "../api/types";
import { unitDesignations } from "../i18n/designation";
import { k, locale, t, tServer } from "../i18n/i18n";
import { missionKey, unitMissionName } from "../i18n/vocabulary";

/** Logique du menu de mission (docs/14 §2.5), sans React : entrées, filtre, état initial de la case « Placer
 *  l'unité selon la mission », restriction « Sans mission » des unités amies, indications. */

export const PHASES: readonly Phase[] = ["T1", "T2", "T3"];

/** Même mission, quelle que soit l'écriture (« S'infiltrer », « S'INFILTRER », « Défendre », « DÉFENDRE »). */
export function sameMission(a: string | null | undefined, b: string | null | undefined): boolean {
  return missionKey(a ?? "") === missionKey(b ?? "");
}

/** Initiales d'un verbe sans miniature connue (pastille neutre, jamais la flèche « Attaquer » de repli) :
 *  « Coup d'arrêt » → « CA », « Stop » → « ST ». */
export function missionInitials(label: string): string {
  const plain = label.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();
  const words = plain.split(/[\s'’-]+/).filter((w) => w.length > 1);
  if (words.length === 0) return plain.slice(0, 2) || "?";
  if (words.length === 1) return words[0]!.slice(0, 2);
  return words
    .slice(0, 3)
    .map((w) => w.charAt(0))
    .join("");
}

/** Tâche permanente d'une unité sans mission (registre A95) : un poste de commandement conduit l'opération à chaque
 *  temps, les trains de combat soutiennent ; ni mission, ni « Sans mission », ni menu : seule la localité de chaque
 *  temps, et cette mention unique. */
const STANDING_TASK: Record<NonNullable<DeployedUnit["standing_task"]>, string> = {
  command: k("Conduite de l'opération"),
  sustainment: k("Soutien logistique"),
};

/** Mention de la tâche permanente de l'unité (traduite), null pour une unité à mission. */
export function standingTaskLabel(unit: Pick<DeployedUnit, "standing_task">): string | null {
  return unit.standing_task ? t(STANDING_TASK[unit.standing_task]) : null;
}

/** Texte replié pour le filtre : minuscules sans accents. */
export function foldText(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export interface CatalogueItem {
  code: string;
  label: string;
  image: string | null;
}

/** Missions de l'échelon pour le camp de l'unité, triées par libellé affiché, filtrées par la saisie (libellé ou
 *  code, sans accents ni casse). */
export function catalogueItems(catalogue: readonly MissionCatalogueEntry[] | undefined, query = ""): CatalogueItem[] {
  const needle = foldText(query);
  return (catalogue ?? [])
    .map((entry) => ({ code: entry.code, label: unitMissionName(entry.code), image: entry.image }))
    .filter((item) => !needle || foldText(item.label).includes(needle) || foldText(item.code).includes(needle))
    .sort((a, b) => a.label.localeCompare(b.label, locale(), { sensitivity: "base" }));
}

/** « Sans mission » refusé pour une unité amie quand les deux autres temps sont déjà sans mission : une unité amie
 *  sans aucune mission n'est plus dessinée (`deployment.py`). Une unité ennemie n'a pas cette restriction. */
export function noMissionBlocked(unit: Pick<DeployedUnit, "side" | "positions">, phase: Phase): boolean {
  if (unit.side !== "AMI") return false;
  return PHASES.filter((p) => p !== phase).every((p) => !unit.positions[p]?.mission);
}

/** État initial de la case « Placer l'unité selon la mission » (K15) : cochée si l'unité est déjà placée selon la
 *  mission à ce temps ; décochée, position choisie à la main conservée, si elle a été déplacée à la main ; sinon
 *  la préférence du poste (cochée par défaut). */
export function initialReposition(
  position: Pick<UnitPosition, "anchored" | "moved"> | undefined,
  preference: boolean,
): { checked: boolean; kept: boolean } {
  if (position?.anchored) return { checked: true, kept: false };
  if (position?.moved) return { checked: false, kept: true };
  return { checked: preference, kept: false };
}

const HIDES_ENEMY_ARROWS = new Set(["DETRUIRE", "NEUTRALISER"]);

/** Indication discrète sous une entrée : DÉTRUIRE et NEUTRALISER d'une unité amie masquent les flèches de mission
 *  ennemies de ce temps (comportement de l'iPad, `mission_symbols.py`) ; RECONNAÎTRE est la mission d'une unité amie
 *  au rôle de reconnaissance (registre A124). Clé à traduire, ou null. */
export function missionHint(code: string, side: DeployedUnit["side"], role?: DeployedUnit["tactical_role"]): string | null {
  if (side !== "AMI") return null;
  if (role === "reco" && missionKey(code) === RECO_KEY) return k("mission du rôle de reconnaissance");
  return HIDES_ENEMY_ARROWS.has(missionKey(code)) ? k("masque les flèches de mission ennemies de ce temps") : null;
}

const RECO_KEY = "RECONNAITRE";

/** Unité amie au rôle de reconnaissance (étiquette « Reco ») : sa mission est RECONNAÎTRE à chaque temps (registre
 *  A124) ; le menu le rappelle et place RECONNAÎTRE en tête des missions de l'échelon. */
export function isRecoUnit(unit: Pick<DeployedUnit, "side" | "tactical_role" | "support">): boolean {
  return unit.side === "AMI" && unit.tactical_role === "reco" && !unit.support;
}

/** Missions de l'échelon d'une unité de reconnaissance : RECONNAÎTRE d'abord, les autres dans l'ordre du libellé. */
export function recoFirst<T extends { code: string }>(items: readonly T[]): T[] {
  return [...items.filter((i) => missionKey(i.code) === RECO_KEY), ...items.filter((i) => missionKey(i.code) !== RECO_KEY)];
}

const PREFERENCE_KEY = "generex-mission-reposition";

/** Préférence du poste pour « Placer l'unité selon la mission » (cochée par défaut). */
export function readRepositionPreference(): boolean {
  try {
    return window.localStorage.getItem(PREFERENCE_KEY) !== "0";
  } catch {
    return true;
  }
}

export function writeRepositionPreference(on: boolean): void {
  try {
    window.localStorage.setItem(PREFERENCE_KEY, on ? "1" : "0");
  } catch {
    /* stockage indisponible : préférence non mémorisée */
  }
}

// --- Position du menu ------------------------------------------------------------------------------------------

/** Rectangle d'ancrage en coordonnées d'écran (chevron, ou point du menu de la carte). */
export interface MenuAnchor {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export const MENU_WIDTH = 296;
const MARGIN = 8;
const MIN_HEIGHT = 200;

/** Position du menu : sous l'ancre, sinon au-dessus ; si aucun côté n'offre la hauteur minimale, le menu est
 *  recalé dans la fenêtre (il peut alors couvrir l'ancre) — jamais hors de l'écran. */
export function placement(anchor: MenuAnchor, vw: number, vh: number) {
  const left = Math.max(MARGIN, Math.min(anchor.left, vw - MENU_WIDTH - MARGIN));
  const below = vh - anchor.bottom - MARGIN - 4;
  const above = anchor.top - MARGIN - 4;
  const down = below >= Math.min(380, above);
  const room = down ? below : above;
  if (room < MIN_HEIGHT) {
    const height = Math.min(MIN_HEIGHT, vh - 2 * MARGIN);
    const top = down ? Math.max(MARGIN, vh - MARGIN - height) : MARGIN;
    return { left, top, maxHeight: Math.min(height, vh - top - MARGIN), origin: down ? "top left" : "bottom left" };
  }
  return down
    ? { left, top: anchor.bottom + 4, maxHeight: below, origin: "top left" }
    : { left, bottom: vh - anchor.top + 4, maxHeight: above, origin: "bottom left" };
}


/** Notes du serveur qui expliquent un placement selon la mission non obtenu (`deployment.notes`, en français :
 *  `reprojection.ANCHOR_MISSING_NOTE`, `deployment.ANCHOR_WITHOUT_CORRIDOR_NOTE`). */
export const ANCHOR_NOTES: readonly string[] = [
  "Aucune position doctrinale pour cette mission : l'unité reste en place",
  "Fuseau non tracé : unités laissées à leur position doctrinale",
];

/** Position changée par l'écriture (au-delà d'un centimètre environ). */
const displaced = (a: Pick<UnitPosition, "lat" | "lon">, b: Pick<UnitPosition, "lat" | "lon">) =>
  Math.abs(a.lat - b.lat) > 1e-7 || Math.abs(a.lon - b.lon) > 1e-7;

const sentence = (text: string) => (/[.!?…]$/.test(text) ? text : `${text}.`);

/** Détail de l'avis d'un choix de mission (§2.5), traduit : rien n'est tu. « Placer l'unité selon la mission »
 *  demandé mais l'unité n'est pas placée à ce temps : la note du serveur qui l'explique (sinon une phrase
 *  générique) ; puis les notes apparues avec cette écriture. Les notes d'ancrage ne sont reprises que pour l'unité
 *  choisie : elles valent pour tous les ancrages du dossier et pourraient viser une autre unité.
 *  Le serveur retire alors la position posée à la main et l'ancrage précédent (`routes/deployment.py`, PUT
 *  mission) : une unité déplacée ou ancrée revient à sa position doctrinale. L'avis le dit, au lieu de la note
 *  « l'unité reste en place » qui serait fausse, et rappelle ⌘Z. */
export function missionChoiceNotes(
  write: { result: Pick<Deployment, "notes" | "units">; previous: Pick<Deployment, "notes"> & Partial<Pick<Deployment, "units">> },
  choice: Pick<MissionChoice, "unit" | "phase" | "mission" | "reposition">,
): string | undefined {
  const notes = write.result.notes ?? [];
  const seen = new Set(write.previous.notes ?? []);
  const out: string[] = [];
  const position = write.result.units.find((u) => u.key === choice.unit)?.positions[choice.phase];
  if (choice.reposition && choice.mission && position && !position.anchored) {
    const reason = notes.find((n) => ANCHOR_NOTES.includes(n));
    const before = write.previous.units?.find((u) => u.key === choice.unit)?.positions[choice.phase];
    if (before && (before.moved || before.anchored) && displaced(before, position)) {
      out.push(
        reason === ANCHOR_NOTES[0]
          ? t("Aucune position doctrinale pour cette mission : l'unité revient à sa position doctrinale (⌘Z pour l'annuler)")
          : t("L'unité n'a pas pu être placée selon la mission : elle revient à sa position doctrinale (⌘Z pour l'annuler)"),
      );
    } else {
      out.push(reason ? unitDesignations(tServer(reason)) : t("L'unité n'a pas pu être placée selon la mission : elle reste en place"));
    }
  }
  for (const note of notes) {
    if (!seen.has(note) && !ANCHOR_NOTES.includes(note)) out.push(unitDesignations(tServer(note)));
  }
  const text = out.map((n) => n.trim()).filter(Boolean).map(sentence).join(" ");
  return text || undefined;
}
